import {
  ElementRef,
  FC,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useState,
} from "react";
import Select from "react-select";
import Creatable from "react-select/creatable";
import styled from "styled-components";
import _uniqueId from "lodash/uniqueId";
import { getOptionsMaxHeight } from "./dropdown.utils";

export enum DropdownTestIds {
  Component = "dropdown",
}

import {
  Control,
  DropdownContextProvider,
  DropdownIndicator,
  Group,
  GroupHeading,
  Input,
  Menu,
  MenuList,
  Option as OptionComponent,
  Placeholder,
  SingleValue,
} from "./components";
import { alpha } from "../../typography";
import { Option, DropdownVariant, DropdownProps } from "./types";
import { NoOptionsMessage } from "./components/NoOptionsMessage";
import { ClearIndicator } from "./components/ClearIndicator";
import { useDropdown } from "./useDropdown";

type LabelProps = {
  $isDisabled: boolean;
  $hasError: boolean;
};

export const Label = styled.div<LabelProps>`
  margin-bottom: 4px;
  ${({ $isDisabled }) =>
    $isDisabled ? alpha.primaryInactive.body3 : alpha.primaryActive.body3};
  ${({ $hasError }) => $hasError && alpha.error.body3};
`;

export const Asterisk = styled.span`
  ${alpha.error.body3};
`;

export const ErrorMessage = styled.div`
  ${alpha.error.caption};
`;

const overrideComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorSeparator: () => null,
  Input,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option: OptionComponent,
  Placeholder,
  SingleValue,
};

type DropdownContainerProps = {
  id: string;
  testId?: string;
  label?: string;
  labelId?: string;
  containerId?: string;
  variant: DropdownVariant;
  isRequired: boolean;
  errorMessage: string;
  allowIcons: boolean;
  hasSelectedOption?: boolean;
  children: ReactNode;
  name: string;
  optionsClassName?: string;
};

export const DropdownContainer: FC<DropdownContainerProps> = ({
  id,
  name,
  testId,
  label,
  labelId,
  variant,
  isRequired,
  errorMessage,
  allowIcons,
  hasSelectedOption,
  children,
  optionsClassName = "",
}) => {
  const hasError = errorMessage.length > 0;
  return (
    <div id={id} data-testid={testId ?? DropdownTestIds.Component}>
      <label htmlFor={name || id}>
        {label && (
          <Label
            $isDisabled={variant === "DISABLED" || variant === "READONLY"}
            $hasError={hasError}
            id={labelId}
          >
            {label} {isRequired ? <Asterisk>*</Asterisk> : null}
          </Label>
        )}
        <DropdownContextProvider
          allowIcons={allowIcons}
          hasError={hasError}
          variant={variant}
          hasSelectedOption={hasSelectedOption}
          optionsClassName={optionsClassName}
        >
          {children}
        </DropdownContextProvider>
      </label>
      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
};

// after we upgrade to react 19 we can remove forwardRef
// https://react.dev/blog/2024/12/05/react-19#ref-as-a-prop
// technically we could pass the ref with a different prop name and it seems to work
// but i dont want to introduce subtle, hard to detect bugs that might be related to
// passing the ref as a prop with a different name
export const Dropdown = forwardRef<ElementRef<typeof Select>, DropdownProps>(
  (
    {
      id,
      testId,
      label,
      labelId,
      containerId,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      styles,
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof Select>>
  ) => {
    const [generatedId] = useState(_uniqueId("single-select-"));
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id || generatedId,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;

    return (
      <DropdownContainer
        id={id ?? generatedId}
        testId={testId}
        label={label}
        labelId={labelId}
        containerId={containerId}
        variant={variant}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
        name={name}
      >
        <Select
          maxMenuHeight={getOptionsMaxHeight(maxOptionsToShow, options)}
          {...dropdownProps}
          ref={ref}
          id={containerId ? containerId : id}
          name={name}
          components={overrideComponents}
          closeMenuOnSelect
          value={value}
          onChange={(value, action) => {
            if (value && action.action === "select-option") {
              const option = value as Option;
              if (option !== selected) {
                setSelected(option);
                onSelect({ selected: option });
              }
            }
            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues?.[0] as Option });
            }
          }}
          styles={{
            ...styles,
            menu: (provided) => ({
              ...provided,
              zIndex: 10000,
            }),
          }}
        />
      </DropdownContainer>
    );
  }
);

// after we upgrade to react 19 we can remove forwardRef
// https://react.dev/blog/2024/12/05/react-19#ref-as-a-prop
export const CreatableDropdown = forwardRef<
  ElementRef<typeof Creatable>,
  DropdownProps
>(
  (
    {
      id,
      testId,
      label,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      styles,
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof Creatable>>
  ) => {
    const [generatedId] = useState(_uniqueId("single-select-"));
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id || generatedId,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;

    return (
      <DropdownContainer
        id={id ?? generatedId}
        testId={testId}
        label={label}
        variant={variant}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
        name={name}
      >
        <Creatable
          maxMenuHeight={getOptionsMaxHeight(maxOptionsToShow)}
          {...dropdownProps}
          ref={ref}
          id={id}
          name={name}
          components={overrideComponents}
          closeMenuOnSelect
          value={value}
          onChange={(value, action) => {
            if (
              value &&
              (action.action === "select-option" ||
                action.action === "create-option")
            ) {
              const option = value as Option;
              if (option !== selected) {
                setSelected(option);
                onSelect({ selected: option });
              }
            }
            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues?.[0] as Option });
            }
          }}
          styles={{
            ...styles,
            menu: (provided) => ({
              ...provided,
              zIndex: 10000,
            }),
          }}
        />
      </DropdownContainer>
    );
  }
);
